import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
  Keyboard,
} from 'react-native';
import { theme } from '../theme/theme';
import { SearchBar } from '../components/search/SearchBar';
import { FilterModal, SearchFilters } from '../components/search/FilterModal';
import { SearchSuggestions } from '../components/search/SearchSuggestions';
import { PinCard } from '../components/PinCard';
import { BackIcon } from '../components/icons';
import { SearchHistoryManager } from '../utils/searchHistory';

interface Artwork {
  id: string;
  title: string;
  imageUrl: string;
  artist: {
    id: string;
    name: string;
    username: string;
  };
  likesCount: number;
  viewsCount: number;
  price?: number;
  currency?: string;
  isForSale: boolean;
  createdAt: string;
}

interface SearchPageProps {
  onBack?: () => void;
  initialQuery?: string;
}

export const SearchPage: React.FC<SearchPageProps> = ({
  onBack,
  initialQuery = '',
}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  
  // 搜索建议相关状态
  const [showSuggestions, setShowSuggestions] = useState(!initialQuery);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const searchArtworks = useCallback(async (
    query: string = searchQuery,
    currentFilters: SearchFilters = filters,
    pageNum: number = 1,
    append: boolean = false
  ) => {
    if (pageNum === 1) {
      setLoading(true);
    }

    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '20',
        ...(query && { searchQuery: query }),
        ...(currentFilters.category && { category: currentFilters.category }),
        ...(currentFilters.isForSale !== undefined && { isForSale: currentFilters.isForSale.toString() }),
        ...(currentFilters.priceMin !== undefined && { priceMin: currentFilters.priceMin.toString() }),
        ...(currentFilters.priceMax !== undefined && { priceMax: currentFilters.priceMax.toString() }),
        ...(currentFilters.medium && { medium: currentFilters.medium }),
        ...(currentFilters.sortBy && { sortBy: currentFilters.sortBy }),
        ...(currentFilters.sortOrder && { sortOrder: currentFilters.sortOrder }),
      });

      const response = await fetch(`http://localhost:3001/artworks?${params}`);
      const result = await response.json();

      if (response.ok) {
        const newArtworks = result.artworks || [];
        setArtworks(prev => append ? [...prev, ...newArtworks] : newArtworks);
        setTotal(result.total || 0);
        setHasMore(newArtworks.length === 20);
        setPage(pageNum);
      } else {
        console.error('Search error:', result.message);
      }
    } catch (error) {
      console.error('Search request error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery, filters]);

  // 加载搜索历史
  useEffect(() => {
    loadRecentSearches();
    if (initialQuery) {
      searchArtworks();
    }
  }, []);

  // 监听搜索查询变化，获取建议
  useEffect(() => {
    if (searchQuery.trim() && isSearchFocused) {
      getSuggestions(searchQuery);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, isSearchFocused]);

  const loadRecentSearches = async () => {
    try {
      const history = await SearchHistoryManager.getSearchHistory();
      setRecentSearches(history);
    } catch (error) {
      console.error('Error loading recent searches:', error);
    }
  };

  const getSuggestions = async (query: string) => {
    try {
      const historySuggestions = await SearchHistoryManager.getSearchSuggestions(query);
      setSuggestions(historySuggestions);
    } catch (error) {
      console.error('Error getting suggestions:', error);
    }
  };

  const handleSearch = async (query: string) => {
    if (query.trim()) {
      // 添加到搜索历史
      await SearchHistoryManager.addSearchHistory(query.trim());
      await loadRecentSearches();
    }
    
    setSearchQuery(query);
    setShowSuggestions(false);
    setPage(1);
    Keyboard.dismiss();
    searchArtworks(query, filters, 1, false);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    handleSearch(suggestion);
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    setShowSuggestions(true);
  };

  const handleSearchBlur = () => {
    setIsSearchFocused(false);
    // 延迟隐藏建议，允许用户点击建议
    setTimeout(() => setShowSuggestions(false), 150);
  };

  const handleClearRecentSearches = async () => {
    await SearchHistoryManager.clearSearchHistory();
    await loadRecentSearches();
  };

  const handleRemoveRecentSearch = async (search: string) => {
    await SearchHistoryManager.removeSearchHistory(search);
    await loadRecentSearches();
  };

  const handleApplyFilters = (newFilters: SearchFilters) => {
    setFilters(newFilters);
    setPage(1);
    searchArtworks(searchQuery, newFilters, 1, false);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setPage(1);
    searchArtworks(searchQuery, filters, 1, false);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      searchArtworks(searchQuery, filters, nextPage, true);
    }
  };

  const hasActiveFilters = () => {
    return Object.keys(filters).some(key => {
      const value = filters[key as keyof SearchFilters];
      return value !== undefined && value !== '' && value !== null;
    });
  };

  const renderArtwork = ({ item }: { item: Artwork }) => (
    <PinCard
      artwork={{
        id: item.id,
        title: item.title,
        image: item.imageUrl,
        artist: {
          id: item.artist.id,
          name: item.artist.name,
          avatar: item.artist.username, // 临时使用username作为avatar
        },
        stats: {
          likes: item.likesCount,
          comments: 0, // 临时设为0
        },
        isLiked: false,
        isBookmarked: false,
      }}
      onPress={() => {
        console.log('Navigate to artwork:', item.id);
      }}
      onArtistPress={() => {
        console.log('Navigate to artist:', item.artist.id);
      }}
    />
  );

  const renderHeader = () => (
    <View style={styles.resultsHeader}>
      <Text style={styles.resultsText}>
        {loading && page === 1 ? '搜索中...' : `找到 ${total} 件作品`}
      </Text>
      {searchQuery && (
        <Text style={styles.queryText}>
          搜索: "{searchQuery}"
        </Text>
      )}
    </View>
  );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyTitle}>未找到相关作品</Text>
      <Text style={styles.emptyText}>
        尝试调整搜索关键词或筛选条件
      </Text>
    </View>
  );

  const renderFooter = () => {
    if (!loading || page === 1) return null;
    
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator color={theme.colors.primary} />
        <Text style={styles.loadingText}>加载更多...</Text>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        {onBack && (
          <TouchableOpacity onPress={onBack} style={styles.backButton}>
            <BackIcon size={24} color={theme.colors.text} />
          </TouchableOpacity>
        )}
        <View style={styles.headerContent}>
          <SearchBar
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSearch={handleSearch}
            onFilterPress={() => setShowFilterModal(true)}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            hasActiveFilters={hasActiveFilters()}
            showFilter={true}
          />
        </View>
      </View>

      {/* 搜索建议 */}
      <SearchSuggestions
        visible={showSuggestions}
        suggestions={suggestions}
        recentSearches={recentSearches}
        onSelectSuggestion={handleSelectSuggestion}
        onClearRecentSearches={handleClearRecentSearches}
        onRemoveRecentSearch={handleRemoveRecentSearch}
      />

      <FlatList
        data={artworks}
        renderItem={renderArtwork}
        keyExtractor={(item) => item.id}
        numColumns={2}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={!loading ? renderEmpty : null}
        ListFooterComponent={renderFooter}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[theme.colors.primary]}
          />
        }
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.1}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.listContent}
      />

      {loading && page === 1 && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      )}

      <FilterModal
        visible={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.bg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: theme.spacing.md,
  },
  headerContent: {
    flex: 1,
  },
  resultsHeader: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  resultsText: {
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.medium,
    color: theme.colors.text,
  },
  queryText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.xs,
  },
  listContent: {
    paddingHorizontal: theme.spacing.md,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing.xl * 2,
  },
  emptyTitle: {
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.text,
    marginBottom: theme.spacing.sm,
  },
  emptyText: {
    fontSize: theme.fontSize.base,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  footerLoader: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing.lg,
  },
  loadingText: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSize.sm,
    color: theme.colors.textSecondary,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
